import { Link } from "react-router-dom";
import { Controller } from "react-hook-form";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useEditPage } from "@/hooks/useEditPage";
import ErrorBanner from "@/components/common/ErrorBanner";
import { premiumUpgradeCreateSchema } from "@ckgo2/shared/schemas";
import type { PremiumUpgrade } from "@ckgo2/shared/types";
import MediaPicker from "@/components/common/MediaPicker";

interface FormData {
  title: string;
  description: string;
  imageId: string | null;
  badge: string;
  sortOrder: number;
  isActive: boolean;
}

export default function PremiumUpgradeEditPage() {
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    FormData,
    PremiumUpgrade
  >({
    resourceKey: "premiumUpgrade",
    listKey: "premiumUpgrades",
    apiPath: "/api/admin/premium-upgrades",
    schema: premiumUpgradeCreateSchema,
    defaultValues: {
      title: "",
      description: "",
      imageId: null,
      badge: "",
      sortOrder: 0,
      isActive: true,
    },
    navigateTo: "/premium-upgrades",
    transformOnLoad: (u) => ({
      title: u.title || "",
      description: u.description || "",
      imageId: u.imageId ?? null,
      badge: u.badge || "",
      sortOrder: u.sortOrder || 0,
      isActive: u.isActive ?? true,
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!isNew && !isLoading && !itemData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-lg text-gray-600 dark:text-gray-400">Upgrade not found</p>
        <Link to="/premium-upgrades" className="btn-primary">
          <ArrowLeft className="h-5 w-5" />
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/premium-upgrades"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "New Premium Upgrade" : "Edit Premium Upgrade"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew ? "Create a new upgrade option" : `Editing: ${itemData?.title}`}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          className="btn-primary"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Save
            </>
          )}
        </button>
      </div>

      <ErrorBanner error={saveMutation.error} />

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <div>
              <label htmlFor="title" className="label">
                Title *
              </label>
              <input
                {...register("title")}
                type="text"
                id="title"
                className="input"
                placeholder="e.g. Business Class Flight Upgrade"
              />
            </div>

            <div>
              <label htmlFor="description" className="label">
                Description
              </label>
              <textarea
                {...register("description")}
                id="description"
                rows={4}
                className="input"
                placeholder="Describe the upgrade..."
              />
            </div>

            <div>
              <label className="label">Image</label>
              <Controller
                name="imageId"
                control={control}
                render={({ field }) => (
                  <MediaPicker
                    mode="single"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    folder="general"
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card space-y-4">
            <div>
              <label htmlFor="badge" className="label">
                Badge
              </label>
              <select {...register("badge")} id="badge" className="input">
                <option value="">-- No badge --</option>
                <option value="POPULAR">POPULAR</option>
                <option value="OUR TIP">OUR TIP</option>
                <option value="DISCOUNT">DISCOUNT</option>
                <option value="NEW">NEW</option>
              </select>
            </div>

            <div>
              <label htmlFor="sortOrder" className="label">
                Sort Order
              </label>
              <input
                {...register("sortOrder", { valueAsNumber: true })}
                type="number"
                id="sortOrder"
                className="input"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                {...register("isActive")}
                type="checkbox"
                id="isActive"
                className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
              />
              <label
                htmlFor="isActive"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Active
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
